import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ErrorLogTable = ({ errors, onErrorSelect, onStatusUpdate, selectedError }) => {
  const [expandedError, setExpandedError] = useState(null);

  const getSeverityBadge = (severity) => {
    const badges = {
      critical: 'bg-red-100 text-red-800 border-red-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-blue-100 text-blue-800 border-blue-200'
    };
    return badges?.[severity] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusBadge = (status) => {
    const badges = {
      new: 'bg-red-100 text-red-800',
      acknowledged: 'bg-yellow-100 text-yellow-800',
      investigating: 'bg-blue-100 text-blue-800',
      resolved: 'bg-green-100 text-green-800',
      auto_recovered: 'bg-purple-100 text-purple-800',
      failed_recovery: 'bg-red-100 text-red-800'
    };
    return badges?.[status] || 'bg-gray-100 text-gray-800';
  };

  const getRecoveryStatusBadge = (status) => {
    const badges = {
      pending: 'bg-gray-100 text-gray-800',
      in_progress: 'bg-blue-100 text-blue-800',
      succeeded: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      manual_required: 'bg-orange-100 text-orange-800'
    };
    return badges?.[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleString();
  };

  const toggleExpand = (errorId) => {
    setExpandedError(expandedError === errorId ? null : errorId);
  };

  if (!errors || errors?.length === 0) {
    return (
      <div className="text-center py-12">
        <Icon name="CheckCircle" size={48} className="mx-auto text-green-500 mb-4" />
        <h3 className="text-lg font-medium text-neutral-900 mb-2">No Errors Found</h3>
        <p className="text-neutral-600">System is running smoothly with no errors to display</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {errors?.map((error) => (
        <div
          key={error?.id}
          className={`border rounded-lg transition-all ${
            selectedError?.id === error?.id
              ? 'border-blue-500 bg-blue-50' :'border-neutral-200 bg-white hover:border-neutral-300'
          }`}
        >
          {/* Error Header */}
          <div
            className="p-4 cursor-pointer"
            onClick={() => onErrorSelect(error)}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded border ${getSeverityBadge(error?.severity)}`}>
                    {error?.severity?.toUpperCase()}
                  </span>
                  <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusBadge(error?.errorStatus)}`}>
                    {error?.errorStatus?.replace('_', ' ')?.toUpperCase()}
                  </span>
                  <span className={`px-2 py-1 text-xs font-medium rounded ${getRecoveryStatusBadge(error?.recoveryStatus)}`}>
                    {error?.recoveryStatus?.replace('_', ' ')}
                  </span>
                  {error?.errorCode && (
                    <span className="px-2 py-1 text-xs font-mono bg-neutral-100 text-neutral-700 rounded">
                      {error?.errorCode}
                    </span>
                  )}
                </div>
                <h3 className="text-sm font-semibold text-neutral-900 mb-1 truncate">
                  {error?.errorMessage}
                </h3>
                <div className="flex items-center gap-4 text-xs text-neutral-600">
                  <span className="flex items-center gap-1">
                    <Icon name="Package" size={14} />
                    {error?.affectedModule || 'Unknown Module'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="User" size={14} />
                    {error?.userEmail || 'System'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="Clock" size={14} />
                    {formatDate(error?.occurredAt)}
                  </span>
                  {error?.category && (
                    <span className="flex items-center gap-1">
                      <Icon name="Tag" size={14} />
                      {error?.category}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e?.stopPropagation();
                    toggleExpand(error?.id);
                  }}
                  className="p-2 hover:bg-neutral-100 rounded transition-colors"
                  title="View Details"
                >
                  <Icon name={expandedError === error?.id ? 'ChevronUp' : 'ChevronDown'} size={18} />
                </button>
              </div>
            </div>
          </div>

          {/* Expanded Details */}
          {expandedError === error?.id && (
            <div className="border-t border-neutral-200 p-4 bg-neutral-50">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <h4 className="text-xs font-semibold text-neutral-700 mb-2">Error Details</h4>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-neutral-600">Type:</span>
                      <span className="font-medium text-neutral-900">{error?.errorType?.replace('_', ' ')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-600">Severity:</span>
                      <span className="font-medium text-neutral-900">{error?.severity}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-600">Status:</span>
                      <span className="font-medium text-neutral-900">{error?.errorStatus}</span>
                    </div>
                    {error?.requestMethod && (
                      <div className="flex justify-between">
                        <span className="text-neutral-600">Method:</span>
                        <span className="font-medium text-neutral-900">{error?.requestMethod}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-neutral-700 mb-2">Recovery Status</h4>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-neutral-600">Status:</span>
                      <span className="font-medium text-neutral-900">{error?.recoveryStatus?.replace('_', ' ')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-600">Auto-Recovery:</span>
                      <span className="font-medium text-neutral-900">{error?.autoRecoveryEnabled ? 'Enabled' : 'Disabled'}</span>
                    </div>
                    {error?.recoveryAttemptedAt && (
                      <div className="flex justify-between">
                        <span className="text-neutral-600">Attempted:</span>
                        <span className="font-medium text-neutral-900">{formatDate(error?.recoveryAttemptedAt)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {error?.requestUrl && (
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-neutral-700 mb-2">Request URL</h4>
                  <div className="bg-white p-2 rounded border border-neutral-200">
                    <code className="text-xs text-neutral-800 break-all">{error?.requestUrl}</code>
                  </div>
                </div>
              )}

              {error?.errorStack && (
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-neutral-700 mb-2">Stack Trace</h4>
                  <div className="bg-white p-3 rounded border border-neutral-200 max-h-40 overflow-y-auto">
                    <pre className="text-xs text-neutral-800 whitespace-pre-wrap font-mono">{error?.errorStack}</pre>
                  </div>
                </div>
              )}

              {error?.tags && error?.tags?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-neutral-700 mb-2">Tags</h4>
                  <div className="flex flex-wrap gap-2">
                    {error?.tags?.map((tag, index) => (
                      <span key={index} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2 pt-3 border-t border-neutral-200">
                {error?.errorStatus !== 'resolved' && (
                  <>
                    <button
                      onClick={() => onStatusUpdate(error?.id, 'acknowledged')}
                      className="px-3 py-1.5 text-xs font-medium bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors"
                    >
                      Acknowledge
                    </button>
                    <button
                      onClick={() => onStatusUpdate(error?.id, 'investigating')}
                      className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                    >
                      Investigate
                    </button>
                    <button
                      onClick={() => onStatusUpdate(error?.id, 'resolved')}
                      className="px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                    >
                      Mark Resolved
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ErrorLogTable;
